import { useAtom } from "jotai";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router";

import { GridFilled, List } from "@/assets/icons/ds";
import { SegmentedControl } from "@/components/Interface/SegmentedControl/SegmentedControl";
import { PortalDeviceListFilter } from "@/components/Portal/components/PortalDeviceListFilter/PortalDeviceListFilter";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { FCC } from "@/types";
import { isListViewAtom } from "@/utils/atoms";

interface PortalSearchAndFilterMenuProps {
  /**
   * Search and filter only make sense once there's more than one
   * subscription to search or filter through - with 0 or 1, hide the search
   * bar and the filter button.
   */
  showSearchAndFilter: boolean;
  /** Tiles/table switch - pointless once there are no subscriptions at all. */
  showViewToggle: boolean;
}

export const PortalSearchAndFilterMenu: FCC<PortalSearchAndFilterMenuProps> = ({
  showSearchAndFilter,
  showViewToggle,
}) => {
  const { t } = useTranslation("portal");
  const [searchParams] = useSearchParams();
  const [_isListView, setListView] = useAtom(isListViewAtom);
  const location = useLocation();

  const listRoute = portalRoutes.mySubscriptions.list;

  useEffect(() => {
    if (
      location.pathname === portalRoutes.mySubscriptions.base ||
      location.pathname === portalRoutes.mySubscriptions.tiles
    ) {
      setListView(false);
    }

    if (location.pathname === portalRoutes.mySubscriptions.list) {
      setListView(true);
    }
  }, [location.pathname, setListView]);

  const querySuffix = searchParams.toString()
    ? `?${searchParams.toString()}`
    : "";

  return (
    <PortalDeviceListFilter
      listRoute={listRoute}
      showSearchAndFilter={showSearchAndFilter}
      viewToggle={
        showViewToggle && (
          <SegmentedControl
            options={[
              {
                to: `${portalRoutes.mySubscriptions.tiles}${querySuffix}`,
                end: true,
                icon: <GridFilled />,
                ariaLabel: t("my-subscriptions.viewToggle.tiles"),
              },
              {
                to: `${portalRoutes.mySubscriptions.list}${querySuffix}`,
                icon: <List />,
                ariaLabel: t("my-subscriptions.viewToggle.list"),
              },
            ]}
          />
        )
      }
    />
  );
};
